/**
 * Ввод часов в ячейку (день × проект): значение + ВСЕ гейты сохранения.
 *
 * Вынесено из `TimesheetGrid.tsx`, чтобы правила были ОДНИ для всех трёх UI:
 * - сеточная ячейка на десктопе (`EntryCell`);
 * - мобильный список по дням (тот же `EntryCell`);
 * - мобильный drawer-редактор heatmap-сетки («заход 2»).
 *
 * Гейты: диапазон 0..24; удаление строки при 0/пустом; «нет изменений» — без
 * запроса; US-73 (>8 ч за день — подтверждение); US-71 (день активной заявки
 * на отсутствие — модалка с текстом сервера + откат значения).
 */
import { useCallback, useEffect, useState } from "react";
import { Button, Group, Modal, Text } from "@mantine/core";
import dayjs from "dayjs";

import { createEntry, deleteEntry, updateEntry, type TimesheetEntry } from "../api/client";
import { formatHours } from "../utils/format";

// US-73: ввод больше нормы 8 ч/день в одну ячейку требует подтверждения —
// защита от опечаток (80 вместо 8, 12 вместо 1.2). Не блокирует переработку,
// только переспрашивает.
export const DAY_HOURS_CONFIRM_THRESHOLD = 8;

/**
 * Итог попытки сохранения. Нужен вызывающему UI, чтобы решить, можно ли
 * закрывать редактор: на `confirm`/`blocked`/`invalid` закрывать НЕЛЬЗЯ —
 * иначе размонтируется модалка подтверждения/блокировки и пользователь
 * не увидит вопрос (поймано тестом мобильного drawer 14.07).
 */
export type CommitResult = "saved" | "deleted" | "confirm" | "blocked" | "invalid" | "noop";

export interface UseEntryPersistArgs {
  entry: TimesheetEntry | null;
  weekId: number;
  projectId: number;
  date: string;
  onChanged: () => void;
}

export function useEntryPersist({
  entry,
  weekId,
  projectId,
  date,
  onChanged,
}: UseEntryPersistArgs) {
  const [value, setValue] = useState<number | string>(entry ? parseFloat(entry.hours) : "");
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<number | null>(null);
  const [blockMsg, setBlockMsg] = useState<string | null>(null);

  // Синхронизация с сервера только при смене identity строки (id/часы),
  // а не на каждый ре-рендер — иначе затирается значение во время правки.
  const entryId = entry?.id ?? null;
  const entryHours = entry?.hours ?? null;
  useEffect(() => {
    setValue(entryHours !== null ? parseFloat(entryHours) : "");
    setValidationError(false);
  }, [entryId, entryHours]);

  /** true — часы сохранены; false — сервер отказал (показана модалка блока). */
  const persist = useCallback(
    async (num: number): Promise<boolean> => {
      const hours = num.toFixed(2);
      if (entry && hours === parseFloat(entry.hours).toFixed(2)) return true;
      setSaving(true);
      try {
        if (entry) {
          await updateEntry(weekId, entry.id, { hours });
        } else {
          await createEntry(weekId, { work_date: date, project: projectId, hours });
        }
        onChanged();
        return true;
      } catch (e) {
        setBlockMsg(e instanceof Error ? e.message : String(e));
        setValue(entry ? parseFloat(entry.hours) : "");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [entry, weekId, projectId, date, onChanged],
  );

  const commit = useCallback(async (): Promise<CommitResult> => {
    if (saving) return "noop";
    // Запятая-безопасный парс: пользователь мог ввести «6,5» (русский разделитель).
    const num = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;

    if (!isNaN(num) && (num < 0 || num > 24)) {
      setValidationError(true);
      return "invalid";
    }
    setValidationError(false);

    if ((isNaN(num) || num === 0) && entry) {
      setSaving(true);
      try {
        await deleteEntry(weekId, entry.id);
        onChanged();
      } finally {
        setSaving(false);
      }
      return "deleted";
    }
    if (isNaN(num) || num === 0) return "noop";
    if (entry && num.toFixed(2) === parseFloat(entry.hours).toFixed(2)) return "noop";

    if (num > DAY_HOURS_CONFIRM_THRESHOLD) {
      setPendingConfirm(num);
      return "confirm";
    }
    return (await persist(num)) ? "saved" : "blocked";
  }, [value, entry, saving, weekId, onChanged, persist]);

  const confirmPending = useCallback(async (): Promise<boolean> => {
    const v = pendingConfirm;
    setPendingConfirm(null);
    if (v === null) return false;
    return await persist(v);
  }, [pendingConfirm, persist]);

  return {
    value,
    setValue,
    saving,
    validationError,
    setValidationError,
    pendingConfirm,
    setPendingConfirm,
    blockMsg,
    setBlockMsg,
    commit,
    persist,
    confirmPending,
  };
}

/** Модалки-гейты (подтверждение >8 ч и блок списания) — общие для всех UI ввода. */
export function EntryGuardModals({
  date,
  pendingConfirm,
  onCancelConfirm,
  onConfirm,
  blockMsg,
  onCloseBlock,
}: {
  date: string;
  pendingConfirm: number | null;
  onCancelConfirm: () => void;
  onConfirm: () => void;
  blockMsg: string | null;
  onCloseBlock: () => void;
}) {
  return (
    <>
      {/* US-73: подтверждение при списании больше нормы дня (защита от опечаток). */}
      <Modal
        opened={pendingConfirm !== null}
        onClose={onCancelConfirm}
        title="Подтвердите часы"
        centered
        size="sm"
        zIndex={400}
      >
        <Text size="sm">
          Вы вводите <strong>{formatHours(pendingConfirm ?? 0)}</strong> ч за{" "}
          {dayjs(date).format("DD.MM.YYYY")} — это больше нормы 8 ч в день. Сохранить?
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" size="sm" onClick={onCancelConfirm}>
            Изменить
          </Button>
          <Button size="sm" onClick={onConfirm}>
            Сохранить
          </Button>
        </Group>
      </Modal>
      {/* Блок списания: день покрыт активной заявкой на отсутствие (US-71 extended). */}
      <Modal
        opened={blockMsg !== null}
        onClose={onCloseBlock}
        title="Нельзя списать часы"
        centered
        size="sm"
        zIndex={400}
      >
        <Text size="sm">{blockMsg}</Text>
        <Text size="xs" c="dimmed" mt="sm">
          Отозвать заявку на отсутствие можно в разделе «Отсутствия».
        </Text>
        <Group justify="flex-end" mt="md">
          <Button size="sm" onClick={onCloseBlock}>
            Понятно
          </Button>
        </Group>
      </Modal>
    </>
  );
}
